import { createUseStyles, withTheme } from '@glu/theming';
import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import locale from '@glu/locale';
import { CaretLeftIcon, CaretRightIcon } from '@glu/icons-react';
import moment from 'moment';
import { AccessibilityText } from '@glu/utilities-react';
import styles from './MonthNav.styles';
import SelectMonths from './SelectMonths';
import SelectYears from './SelectYears';

const useStyles = createUseStyles(styles);

const MonthNav = ({
  dateMax,
  dateMin,
  first,
  firstFocusedElement,
  head,
  index,
  last,
  monthDropdown,
  setHead,
  yearDropdown,
  yearDropdownMaxYear,
  yearDropdownMinYear,
  ...rest
}) => {
  const classes = useStyles(rest);

  const prevMonth = moment(head).add(-1, 'months');
  const nextMonth = moment(head).add(1, 'months');
  const handlePrevClick = () => { setHead(prevMonth); };
  const handleNextClick = () => { setHead(nextMonth); };
  const handleSelectMonth = (month) => { setHead(moment(head).month(month)); };
  const handleSelectYear = (year) => { setHead(moment(head).year(year)); };
  const buttonRef = useRef();

  /* istanbul ignore next */
  useEffect(() => {
    if (firstFocusedElement) {
      buttonRef.current.focus();
    }
  }, [firstFocusedElement]);

  return (
    <div className={classes.header}>
      {first && (
        <button
          aria-label={locale.get('prevMonth')}
          ref={buttonRef}
          data-qa="previous-button"
          onClick={handlePrevClick}
          className={`${classes.button} ${classes.prev} ${first && classes.first}`}
          type="button"
        >
          <AccessibilityText>
            {locale.get('prevMonth')}
          </AccessibilityText>
          <CaretLeftIcon />
        </button>
      )}
      <div className={classes.title}>
        <div className={classes.select}>
          {monthDropdown
            ? (
              <SelectMonths
                head={head}
                index={index}
                onChange={handleSelectMonth}
              />
            )
            : locale.get(`month${head.month() + 1}`)}
        </div>

        <div className={classes.select}>
          {yearDropdown
            ? (
              <SelectYears
                head={head}
                index={index}
                onChange={handleSelectYear}
                yearDropdownMinYear={yearDropdownMinYear}
                yearDropdownMaxYear={yearDropdownMaxYear}
              />
            )
            : head.format('YYYY')}
        </div>
      </div>
      {last && (
        <button
          aria-label={locale.get('nextMonth')}
          data-qa="next-button"
          onClick={handleNextClick}
          className={`${classes.button} ${classes.next} ${last && classes.last}`}
          type="button"
        >
          <AccessibilityText>
            {locale.get('nextMonth')}
          </AccessibilityText>
          <CaretRightIcon />
        </button>
      )}
    </div>
  );
};

MonthNav.propTypes = {
  dateMax: PropTypes.string.isRequired,
  dateMin: PropTypes.string.isRequired,
  first: PropTypes.bool.isRequired,
  firstFocusedElement: PropTypes.bool.isRequired,
  head: PropTypes.shape({
    format: PropTypes.func,
    month: PropTypes.func
  }).isRequired,
  index: PropTypes.number.isRequired,
  last: PropTypes.bool.isRequired,
  monthDropdown: PropTypes.bool.isRequired,
  setHead: PropTypes.func.isRequired,
  yearDropdown: PropTypes.bool.isRequired,
  yearDropdownMaxYear: PropTypes.number.isRequired,
  yearDropdownMinYear: PropTypes.number.isRequired
};

export default withTheme(MonthNav);
